import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks';
import { ErrorBoundary, Header, ProtectedRoute } from './components';
import { useTokenExpiryHandler } from './utils/redirects';
import { LoginPage, SignUpPage, ForgotPasswordPage, ResetPasswordPage, TermsPage, PrivacyPage } from './pages';
import {
  DashboardPage,
  OrderDetailPage,
  ErrorPage,
  NotFoundPage,
  StyleGuidePage,
  BaileysWhatsAppSettingsPage,
  OnboardingPage,
} from './pages';
import './styles/globals.css';

/**
 * Main App component with routing
 */
const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Handle token expiry events and gracefully redirect to login
  useTokenExpiryHandler();

  // If we land on the site with a recovery/access token (e.g. from the email link redirecting to Site URL)
  // we immediately route them to /reset-password while keeping the hash parameters intact.
  const isRecovering = window.location.hash.includes('type=recovery') || 
                       window.location.search.includes('type=recovery');

  if (isRecovering && location.pathname !== '/reset-password') {
    return <Navigate to={`/reset-password${window.location.hash}${window.location.search}`} replace />;
  }

  // Show a full-page loader while we check the existing session.
  // This prevents a flash of the login page on page reload.
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" />
          <p className="text-sm text-slate-500">Loading PrintDeck...</p>
        </div>
      </div>
    );
  }

  const showHeader = isAuthenticated && 
                     location.pathname !== '/forgot-password' && 
                     location.pathname !== '/reset-password' &&
                     location.pathname !== '/login' && 
                     location.pathname !== '/signup' &&
                     location.pathname !== '/terms' &&
                     location.pathname !== '/privacy';

  return (
    <>
      {showHeader && <Header />}
      <Routes>
        {/* Public Auth Routes */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />}
        />
        <Route
          path="/signup"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <SignUpPage />}
        />
        <Route
          path="/forgot-password"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />}
        />
        <Route
          path="/reset-password"
          element={<ResetPasswordPage />}
        />
        <Route
          path="/terms"
          element={<TermsPage />}
        />
        <Route
          path="/privacy"
          element={<PrivacyPage />}
        />

        {/* Protected routes */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders/:id"
          element={
            <ProtectedRoute>
              <OrderDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/whatsapp"
          element={
            <ProtectedRoute>
              <BaileysWhatsAppSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <BaileysWhatsAppSettingsPage />
            </ProtectedRoute>
          }
        />

        {/* Style guide (unprotected for easy access during dev) */}
        <Route path="/style-guide" element={<StyleGuidePage />} />

        {/* Error page */}
        <Route path="/error" element={<ErrorPage />} />

        {/* Fallback routes */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
};

export const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
